import React, { MutableRefObject, ReactElement, useEffect, useRef, useState } from 'react'
import { EMPTY_STRING } from '../../../../../NonReact/constants/Common'
import { EventNames } from '../../../../../NonReact/constants/EventNames'
import { IsStringEmpty, IsVarEmpty, isVarNotEmpty } from '../../../../../NonReact/utils/GlobalFunctions'
import { StringUtils } from '../../../../../NonReact/utils/StringUtils'
import { $addEventListener, $removeEventListener } from '../../../../../NonReact/utils/WindowFunctions'
import { ANY_VALUE_STRING_DEFAULT } from '../../../Constants/DefaultValues'

import Icon, { IconName } from '../../Icon'
import PopperContent from '../../PopperContent'
import TextInputDropDown from '../../TextInputDropDown'

import { MAX_DISPLAYED_RANGE_SEARCH_ITEMS, SelectRangeItemState } from '../Constants'
import { SelectRangeItem } from '../Types'

// CSS imports. Commented out because of current build process.
// import './selectRange.scss'

/**
 * Component properties type.
 */
type Props = {
    items: SelectRangeItem[]
    rangeExpressionValue: string
    onValueChange: (value: string) => void
    specialAnyItemIndex: number
    labelLeft?: string
    searchable?: boolean
    maxFilterListLimit?: Definable<number>
}

const RANGE_EXPRESSION: RegExp = /^\d+\s*-\s*\d+$/
const RANGE_SEPARATOR: string = '-'

/**
 * Select box supporting Akixi proprietary range filter string value.
 *
 * @param items Select box items.
 * @param rangeExpressionValue Current range expression value in text field.
 * @param onValueChange Value change handler.
 * @param specialAnyItem Index index of special "none/any" item.
 * @param labelLeft Label of the range list on left.
 * @param searchable boolean option to allow for searchable items.
 * @param requestAllItems request function to get all items.
 * @param maxFilterListLimit maximum number of items to display in the list.
 */
const SelectRange: React.FC<Props> = ({
    items,
    rangeExpressionValue,
    onValueChange,
    specialAnyItemIndex,
    labelLeft,
    searchable,
    maxFilterListLimit
}: Props): ReactElement => {
    const [isOpen, setIsOpen] = useState<boolean>(false)
    const [searchValue, setSearchValue] = useState<string>('')
    const [currentItemsArr, setCurrentItemsArr] = useState<SelectRangeItem[]>(items)

    const textElemRef: MutableRefObject<null> = useRef(null)
    const inputRef: MutableRefObject<null> = useRef(null)
    const popperRef: MutableRefObject<null> = useRef(null)

    useEffect((): void => {
        if (items) {
            setCurrentItemsArr(items.slice(0, maxFilterListLimit || MAX_DISPLAYED_RANGE_SEARCH_ITEMS))
        }
    }, [items])

    /**
     * Determines if the search value is a range expression.
     *
     * @returns A boolean value.
     */
    const isSearchValueRangeExpression = (): boolean => RANGE_EXPRESSION.test(searchValue)

    /**
     * Is a value within the range.
     *
     * @param searchTerm - range expression separated with a '-'
     * @param value - value to check
     *
     * @returns boolean - true if value is within range
     */
    const isValueWithinRange = (searchTerm: string, value: number): boolean => {
        if (
            IsVarEmpty(value) ||
            IsStringEmpty(searchTerm) ||
            !searchTerm.includes(RANGE_SEPARATOR) ||
            Number.isNaN(value)
        )
            return false

        const rangeValues: string[] = searchTerm.split(RANGE_SEPARATOR)

        if (rangeValues.length !== 2) return false

        const min: number = parseInt(rangeValues[0], 10)
        const max: number = parseInt(rangeValues[1], 10)

        return value >= min && value <= max
    }

    /**
     * Filter items on search value, if search value is a range expression, filter items on range
     * expression
     * @param {SelectRangeItem[]} itemsToFilter - SelectRangeItem[]
     * @returns An array of SelectRangeItem objects
     */
    const filterItemsOnSearch = (): SelectRangeItem[] => {
        const normalisedSearchValue: string = searchValue.replace(/\s|!/g, EMPTY_STRING).toLowerCase()
        const searchTerms: string[] = normalisedSearchValue.split(/,|-/g)
        const rangeTerms: string[] = normalisedSearchValue
            .split(',')
            .filter((term: string): boolean => term.includes('-'))

        const filteredItems: SelectRangeItem[] = items.filter(
            (item: SelectRangeItem, index: number): boolean =>
                index === specialAnyItemIndex ||
                searchTerms.some(
                    (searchTerm: string): boolean =>
                        !!(
                            item.value.replace(' ', EMPTY_STRING).toLowerCase().includes(searchTerm) ||
                            item.description?.replace(' ', EMPTY_STRING)?.toLowerCase()?.includes(searchTerm)
                        )
                ) ||
                rangeTerms.some((rangeTerm: string): boolean => isValueWithinRange(rangeTerm, parseInt(item.value, 10)))
        )

        return filteredItems
    }

    useEffect(() => {
        if (searchValue === EMPTY_STRING) {
            setCurrentItemsArr(items.slice(0, maxFilterListLimit || MAX_DISPLAYED_RANGE_SEARCH_ITEMS))
        } else {
            setCurrentItemsArr(filterItemsOnSearch().slice(0, MAX_DISPLAYED_RANGE_SEARCH_ITEMS))
        }
    }, [searchValue])

    const toCheckBox = (itemState: SelectRangeItemState): ReactElement => {
        switch (itemState) {
            case SelectRangeItemState.INCLUDED:
                return (
                    <div className='state-icon item-included'>
                        <Icon iconName={IconName.CHECKBOX_CHECKED} />
                    </div>
                )
            case SelectRangeItemState.EXCLUDED:
                return (
                    <div className='state-icon item-excluded'>
                        <Icon iconName={IconName.CHECKBOX_MINUS} />
                    </div>
                )
            case SelectRangeItemState.NONE:
            default:
                return (
                    <div className='state-icon item-none'>
                        <Icon iconName={IconName.CHECKBOX_BLANK} />
                    </div>
                )
        }
    }

    const getCurrentSelectLocation = (): [number, number] => {
        const inputReference: HTMLInputElement = inputRef.current as unknown as HTMLInputElement
        const selectionStart: Nullable<number> = inputReference?.selectionStart
        const selectionEnd: Nullable<number> = inputReference?.selectionEnd

        if (
            isVarNotEmpty(selectionStart) &&
            selectionStart === selectionEnd &&
            selectionStart !== rangeExpressionValue.length
        ) {
            return StringUtils.findEntryRange(rangeExpressionValue, selectionStart, ',', '-')
        }

        return [selectionStart ?? 0, selectionEnd ?? 0]
    }

    /**
     * Updates value according to the selected items.
     *
     * @param indexOfUpdatedItem
     * @param itemOldState
     * @param itemNewState
     * @param updatedItem
     * @param newItems
     */
    const updateValue = (
        indexOfUpdatedItem: number | undefined,
        itemOldState: SelectRangeItemState,
        itemNewState: SelectRangeItemState,
        updatedItem: SelectRangeItem | undefined,
        newItems: SelectRangeItem[]
    ): void => {
        const specialAnyItemValue: string = items[specialAnyItemIndex].value

        if (!!updatedItem && itemOldState !== itemNewState) {
            // Item state is actually being changed.

            const updatedItemValue: string = updatedItem.value
            let calculatedRangeExpValue: string = ''

            const currentItems: SelectRangeItem[] = [...newItems]
            // eslint-disable-next-line
            let currentValueAsRangeList: CRangeList = new com.Akixi.Utils.CRangeList(rangeExpressionValue)

            if (
                searchable &&
                itemNewState !== SelectRangeItemState.NONE &&
                indexOfUpdatedItem !== specialAnyItemIndex
            ) {
                const [selectionStart, selectionEnd]: [number, number] = getCurrentSelectLocation()
                const lastIndexComma: number = searchValue.lastIndexOf(',')
                const lastIndexDash: number = searchValue.lastIndexOf('-')

                if (lastIndexComma !== -1 || lastIndexDash !== -1) {
                    const currentSearchTerm: [number] | [number, number] =
                        selectionStart === searchValue.length
                            ? [Math.max(lastIndexComma, lastIndexDash) + 1]
                            : [selectionStart, selectionEnd]

                    // eslint-disable-next-line
                    currentValueAsRangeList = new com.Akixi.Utils.CRangeList(
                        StringUtils.replaceRange(
                            searchValue,
                            updatedItem.value,
                            currentSearchTerm[0],
                            currentSearchTerm.length > 1 ? currentSearchTerm[1] : undefined
                        ).trim()
                    )

                    setSearchValue('')

                    onValueChange(currentValueAsRangeList.toString())

                    return undefined
                }
            }

            let isModified = false

            if (specialAnyItemIndex === indexOfUpdatedItem) {
                // Generic none/all item selected.
                calculatedRangeExpValue = specialAnyItemValue
                setIsOpen(false)
            } else if (updatedItemValue) {
                switch (itemOldState) {
                    case SelectRangeItemState.INCLUDED:
                        isModified = currentValueAsRangeList.RemoveValueInclusion(updatedItemValue)
                        break
                    case SelectRangeItemState.EXCLUDED:
                        isModified = currentValueAsRangeList.RemoveValueExclusion(updatedItemValue)
                        break
                    case SelectRangeItemState.NONE:
                    default:
                        break
                }

                switch (itemNewState) {
                    case SelectRangeItemState.INCLUDED:
                        isModified = currentValueAsRangeList.AddValueInclusion(updatedItemValue)
                        break
                    case SelectRangeItemState.EXCLUDED:
                        isModified = currentValueAsRangeList.AddValueExclusion(updatedItemValue)
                        break
                    case SelectRangeItemState.NONE:
                    default:
                        break
                }
                if (isModified) {
                    if (currentValueAsRangeList.getTotalCount() === 0) {
                        // If there are no items selected, then select the default none/all item.
                        calculatedRangeExpValue = specialAnyItemValue
                    } else {
                        // / Ensure default none/all item is no longer selected.
                        currentValueAsRangeList.RemoveValueInclusion(currentItems[specialAnyItemIndex].value)
                        calculatedRangeExpValue = currentValueAsRangeList.toString()
                    }
                }
            }

            onValueChange(calculatedRangeExpValue)
        }

        return undefined
    }

    /**
     * Maps expression value to the list of items in range select box.
     *
     * @param newRangeExpressionValue the new range expression value.
     * @param itemsToUpdate
     * @param anyItemIndex
     */
    const mapExpressionValueToItems = (
        newRangeExpressionValue: string,
        itemsToUpdate: SelectRangeItem[],
        anyItemIndex: number
    ): SelectRangeItem[] => {
        // eslint-disable-next-line
        const currentValueAsRangeList: CRangeList = new com.Akixi.Utils.CRangeList(newRangeExpressionValue)
        const currentItems: SelectRangeItem[] = [...itemsToUpdate]

        const currentItemsSize: number = currentItems.length

        if (currentItemsSize > 0 && currentItems.length > anyItemIndex) {
            const specialAnyItemValue: string = currentItems[anyItemIndex].value
            let firstSelectedIndex: number = -1

            if (
                currentValueAsRangeList.getTotalCount() === 0 ||
                (currentValueAsRangeList.getTotalCount() === 1 &&
                    currentValueAsRangeList.toString() === specialAnyItemValue)
            ) {
                // Select special item when no item is selected or when the only special one is selected.
                for (let i: number = 0; i < currentItemsSize; i++) {
                    if (anyItemIndex === i) {
                        // Select "none/all" item.
                        firstSelectedIndex = i
                        currentItems[i].state = SelectRangeItemState.INCLUDED
                    }
                    // Unselect all other items.
                    else currentItems[i].state = SelectRangeItemState.NONE
                }
            } else {
                let rangeValue: CRangeValue
                let rangeValuePair: CRangeValuePair
                let rangeValuePairs: CRangeValuePair[]

                let itemState: SelectRangeItemState
                let itemValue: string
                let isItemAlreadyUpdated: boolean

                for (let i: number = 0; i < currentItemsSize; i++) {
                    itemState = SelectRangeItemState.NONE

                    if (anyItemIndex !== i || isSearchValueRangeExpression()) {
                        itemValue = currentItems[i].value

                        if (itemValue) {
                            isItemAlreadyUpdated = false

                            // eslint-disable-next-line
                            rangeValue = new com.Akixi.Utils.CRangeValue(itemValue)
                            // eslint-disable-next-line
                            rangeValuePair = new com.Akixi.Utils.CRangeValuePair(rangeValue, rangeValue)
                            // Check inclusions.
                            rangeValuePairs = currentValueAsRangeList.getInclusions()

                            for (let j = 0; j < currentValueAsRangeList.getInclusionCount(); j++) {
                                if (
                                    rangeValuePairs[j].IsCompatible(rangeValuePair) &&
                                    rangeValuePairs[j].InRange(rangeValue) === 0
                                ) {
                                    isItemAlreadyUpdated = true
                                    itemState = SelectRangeItemState.INCLUDED

                                    if (firstSelectedIndex === -1) firstSelectedIndex = i

                                    break
                                }
                            }
                            if (!isItemAlreadyUpdated) {
                                // Check exclusions.
                                rangeValuePairs = currentValueAsRangeList.getExclusions()

                                for (let j = 0; j < currentValueAsRangeList.getExclusionCount(); j++) {
                                    if (
                                        rangeValuePairs[j].IsCompatible(rangeValuePair) &&
                                        rangeValuePairs[j].InRange(rangeValue) === 0
                                    ) {
                                        isItemAlreadyUpdated = true
                                        itemState = SelectRangeItemState.EXCLUDED

                                        if (firstSelectedIndex === -1) firstSelectedIndex = i

                                        break
                                    }
                                }
                            }
                        }
                    }

                    currentItems[i].state = itemState
                }
            }
        }
        return currentItems
    }

    const handleClickAway = (event?: MouseEvent): void => {
        if (event) {
            const target: HTMLElement = event.target as HTMLElement

            if (
                !(
                    (target &&
                        ((textElemRef?.current as unknown as Node)?.contains(target) ||
                            (popperRef?.current as unknown as Node)?.contains(target))) ||
                    (popperRef?.current as unknown as HTMLElement)?.closest('.popper') === target
                )
            ) {
                setIsOpen(false)
            }
        }
    }

    const handleClickOnTextField = (): void => {
        const inputReference: HTMLInputElement = inputRef.current as unknown as HTMLInputElement
        const [selectStart, selectEnd]: [number, number] = getCurrentSelectLocation()

        inputReference.setSelectionRange(selectStart, selectEnd)
        setSearchValue(rangeExpressionValue.replace('!', '').replace(ANY_VALUE_STRING_DEFAULT, ''))

        setIsOpen(true)
    }

    const handleTextFieldChange = (newValue: string): void => {
        if (searchable) {
            setIsOpen(true)
            setSearchValue(newValue)
        }

        onValueChange(newValue)
    }

    useEffect((): void => {
        if (isOpen) {
            $addEventListener(EventNames.MOUSEDOWN, handleClickAway)
        } else {
            $removeEventListener(EventNames.MOUSEDOWN, handleClickAway)
        }
    }, [isOpen])

    const calculateStateAfterClick = (
        currentState: SelectRangeItemState,
        rightClicked: boolean
    ): SelectRangeItemState => {
        if (rightClicked) {
            return currentState === SelectRangeItemState.EXCLUDED
                ? SelectRangeItemState.NONE
                : SelectRangeItemState.EXCLUDED
        }

        return currentState === SelectRangeItemState.INCLUDED
            ? SelectRangeItemState.NONE
            : SelectRangeItemState.INCLUDED
    }

    const handleItemClick = (clickedItem: SelectRangeItem, rightClick: boolean): void => {
        let oldState: SelectRangeItemState = SelectRangeItemState.NONE
        let newState: SelectRangeItemState = SelectRangeItemState.NONE
        let indexOfUpdatedItem: number | undefined
        let updatedItem: SelectRangeItem | undefined

        const iNone: number = 0
        const newItems: SelectRangeItem[] = items.map((item, index) => {
            if (clickedItem.value === item.value) {
                updatedItem = item
                indexOfUpdatedItem = index
                oldState = item.state ? item.state : SelectRangeItemState.NONE

                if (index !== iNone) {
                    newState = calculateStateAfterClick(oldState, rightClick)
                } else {
                    newState = SelectRangeItemState.INCLUDED
                }

                return {
                    ...item,
                    state: newState
                }
            }

            return item
        })

        updateValue(indexOfUpdatedItem, oldState, newState, updatedItem, newItems)

        return undefined
    }

    const toItem = (item: SelectRangeItem, index: number): ReactElement => {
        const { value, renderElement }: SelectRangeItem = item
        let { state }: SelectRangeItem = item

        if (!state) {
            state = SelectRangeItemState.NONE
        }

        return (
            <div
                key={`${value}-${index}`}
                className='item'
                onClick={(): void => handleItemClick(item, false)}
                onContextMenu={(event): void => {
                    event.preventDefault()
                    handleItemClick(item, true)
                }}
            >
                {toCheckBox(state)}
                {renderElement}
            </div>
        )
    }

    return (
        <React.Fragment>
            <div className='select-range-wrapper'>
                <TextInputDropDown
                    value={rangeExpressionValue}
                    labelLeft={labelLeft}
                    onChange={handleTextFieldChange}
                    elementRef={textElemRef}
                    customDropdown={true}
                    isOpen={isOpen}
                    onClick={handleClickOnTextField}
                    inputRef={inputRef}
                />
            </div>
            <PopperContent anchorEl={textElemRef.current} open={isOpen} setOpen={setIsOpen} isClickAwayHandled={false}>
                <div ref={popperRef}>
                    {isOpen && (
                        <div className='items-wrapper'>
                            {mapExpressionValueToItems(rangeExpressionValue, currentItemsArr, specialAnyItemIndex).map(
                                (value: SelectRangeItem, index: number): ReactElement => toItem(value, index)
                            )}
                        </div>
                    )}
                </div>
            </PopperContent>
        </React.Fragment>
    )
}

export default SelectRange
